import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { ToolsImages } from '../../../assets/images/imageRepository';
import { piper } from '../../../assets/illustrations/illustrationRepository';
import Tool from '../components/Tool';
import mediaQueries from '../../../utils/mediaQueries';

const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: 66px 0;
  @media ${mediaQueries.xlPlusUp} {
    padding-top: 76px;
    padding-bottom: 102px;
  }
`;
const ToolsContainer = styled.div``;
const ToolsList = styled.div`
  display: grid;
  grid-gap: 30px;
  @media ${mediaQueries.xlPlusUp} {
    grid-gap: 0;
  }
`;
const BoldText = styled.span`
  font-weight: bold;
`;
const HelpTextContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 52px;
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 102px;
  }
`;
const HelpText = styled.p`
  color: ${({ theme }) => theme.colors.black3E};
  font-size: 19px;
  font-weight: 600;
  line-height: 26px;
  margin: 0;
  text-align: center;
  max-width: 233px;
  @media ${mediaQueries.xlPlusUp} {
    font-size: 28px;
    line-height: 38px;
    max-width: 100%;
  }
`;

const ToolsSection = () => {
  const intl = useIntl();
  const tools = [
    {
      id: 'toolCreate',
      image: {
        desktop:
          intl.locale === 'es'
            ? ToolsImages.toolCreate.desktopEs
            : ToolsImages.toolCreate.desktop,
        order: 3,
        size: {
          mobile: '312px',
          desktop: '464px',
        },
      },
      title: (
        <FormattedMessage
          id="tools.toolsSection.tool1.title"
          defaultMessage="Create"
        />
      ),
      description: (
        <FormattedMessage
          id="tools.toolsSection.tool1.description"
          defaultMessage="<bText>Piper will show you how to use our powerful Post Editor.</bText> {lineBreak}With free creative designs at your fingertips, and Piper’s help creating professional looking posts, getting started is a snap."
          values={{
            bText: (...chunks) => <BoldText>{chunks}</BoldText>,
            lineBreak: <br />,
          }}
        />
      ),
      piper: {
        image: piper.PiperBackgroundGreen,
        order: 1,
        size: {
          mobile: '158px',
          desktop: '203px',
        },
        marginTop: '30px',
      },
      descriptionMargin: {
        right: '48px',
        left: '48px',
      },
    },
    {
      id: 'toolPost',
      image: {
        desktop:
          intl.locale === 'es'
            ? ToolsImages.toolPost.desktopEs
            : ToolsImages.toolPost.desktop,
        order: 1,
        size: {
          mobile: '312px',
          desktop: '464px',
        },
      },
      title: (
        <FormattedMessage
          id="tools.toolsSection.tool2.title"
          defaultMessage="Post"
        />
      ),
      description: (
        <FormattedMessage
          id="tools.toolsSection.tool2.description"
          defaultMessage="<bText>Our simple scheduler lets you post to all your social accounts at one time.</bText> {lineBreak}Piper will tell you when your customers are most active. You can follow his lead, or set your own publishing schedule."
          values={{
            bText: (...chunks) => <BoldText>{chunks}</BoldText>,
            lineBreak: <br />,
          }}
        />
      ),
      piper: {
        image: piper.PiperBackgroundBlue,
        order: 3,
        size: {
          mobile: '219px',
          desktop: '270px',
        },
        marginTop: '0px',
      },
      descriptionMargin: {
        right: '0px',
        left: '76px',
      },
    },
    {
      id: 'toolBlastoff',
      image: {
        desktop:
          intl.locale === 'es'
            ? ToolsImages.toolBlastoff.desktopEs
            : ToolsImages.toolBlastoff.desktop,
        order: 3,
        size: {
          mobile: '324px',
          desktop: '456px',
        },
      },
      title: (
        <FormattedMessage
          id="tools.toolsSection.tool3.title"
          defaultMessage="Blast off!"
        />
      ),
      description: (
        <FormattedMessage
          id="tools.toolsSection.tool3.description"
          defaultMessage="<bText>One click boosting.</bText> {lineBreak}With Piper’s insights you can easily reach larger audiences to grow your business. Piper will suggest the wisest spend for your social media marketing."
          values={{
            bText: (...chunks) => <BoldText>{chunks}</BoldText>,
            lineBreak: <br />,
          }}
        />
      ),
      piper: {
        image: piper.PiperRocketBackgroundPink,
        order: 1,
        size: {
          mobile: '230px',
          desktop: '285px',
        },
        marginTop: '14px',
      },
      descriptionMargin: {
        right: '86px',
        left: '10px',
      },
    },
    {
      id: 'toolConnect',
      image: {
        desktop:
          intl.locale === 'es'
            ? ToolsImages.toolConnect.desktopEs
            : ToolsImages.toolConnect.desktop,
        order: 1,
        size: {
          mobile: '312px',
          desktop: '464px',
        },
      },
      title: (
        <FormattedMessage
          id="tools.toolsSection.tool4.title"
          defaultMessage="Connect"
        />
      ),
      description: (
        <FormattedMessage
          id="tools.toolsSection.tool4.description"
          defaultMessage="Reply to comments across all platforms from one dashboard. <bText>Increase engagement and connect with your audience in a flash.</bText>"
          values={{
            bText: (...chunks) => <BoldText>{chunks}</BoldText>,
            lineBreak: <br />,
          }}
        />
      ),
      piper: {
        image: piper.PiperBackgroundYellow,
        order: 3,
        size: {
          mobile: '183px',
          desktop: '225px',
        },
        marginTop: '14px',
      },
      descriptionMargin: {
        right: '44px',
        left: '76px',
      },
    },
  ];
  return (
    <Section>
      <ToolsContainer>
        <ToolsList>
          {tools.map((tool) => (
            <Tool tool={tool} key={tool.id} />
          ))}
        </ToolsList>
        <HelpTextContainer>
          <HelpText>
            <FormattedMessage
              id="tools.toolsSection.helpText"
              defaultMessage="Get tailored tips to help your business grow!"
            />
          </HelpText>
        </HelpTextContainer>
      </ToolsContainer>
    </Section>
  );
};

export default ToolsSection;
